import { z } from 'zod';
import {
  PM_TASK_PRIORITIES,
  PM_TASK_STATUSES,
  TASK_DEPENDENCY_TYPES,
  MILESTONE_STATUSES,
  RAID_IMPACTS,
  RAID_ITEM_TYPES,
  RAID_LIKELIHOODS,
  RAID_STATUSES,
  RMA_STATUSES,
  WARRANTY_DECISIONS,
} from './constants.js';
import { NEGOTIABLE_TERMS } from './negotiable-terms.js';

// MUL-2333 (2026-09-08): HTS's default is paid-when-paid, with N30 / N60 as
// the only situational deviations (N45 restored 2026-10-08). `null` in the union represents
// "paid when paid" — the standard — and is also a valid submitted value when
// the sub explicitly asks for it. The 45-day option is gone.
//
// The literals are written explicitly (rather than mapped from
// `NEGOTIABLE_TERMS.paymentTermsDays.allowed`) so the `z.union` infers the
// literal-union output type `30 | 45 | 60 | null` instead of `unknown`. A previous
// `as unknown as [z.ZodTypeAny, ...]` cast made the schema accept the right
// values at runtime but stripped the type so downstream callers
// (`effectiveTerms`, `deviatedKeys`) could not be passed the parsed body.
const paymentTermsDaysLiterals = [
  z.literal(NEGOTIABLE_TERMS.paymentTermsDays.allowed[0]),
  z.literal(NEGOTIABLE_TERMS.paymentTermsDays.allowed[1]),
  z.literal(NEGOTIABLE_TERMS.paymentTermsDays.allowed[2]),
  z.null(),
] as const;

export const RequestedTermsSchema = z
  .object({
    paymentTermsDays: z.union(paymentTermsDaysLiterals).optional(),
    insuranceTier: z.literal(NEGOTIABLE_TERMS.insuranceTier.allowed[0]).optional(),
  })
  .strict();

/** A date STRING that must parse to a real date (rejects `Invalid Date`). */
const validDateString = z
  .string()
  .refine((s) => !Number.isNaN(Date.parse(s)), 'invalid date');

/** Positive integer ID (ConnectWise resource IDs) */
export const positiveIntId = z.coerce.number().int().positive();

/** ISO date string (accepts anything Date.parse understands) or null. */
const isoDateOrNull = z
  .string()
  .refine((s) => !Number.isNaN(Date.parse(s)), 'invalid date')
  .nullable();

/**
 * RMA lifecycle update (#301). Every field optional (partial update), but the
 * body must set at least one so an empty PATCH is a 400 rather than a no-op.
 * Status transitions are additionally validated server-side against the
 * canonical order (see isValidRmaTransition).
 */
export const rmaRecordUpdateSchema = z
  .object({
    status: z.enum(RMA_STATUSES).optional(),
    vendorRmaNumber: z.string().max(200).nullable().optional(),
    returnTracking: z.string().max(200).nullable().optional(),
    replacementReceivedAt: isoDateOrNull.optional(),
    creditAmount: z.number().nonnegative().nullable().optional(),
    creditReceivedAt: isoDateOrNull.optional(),
    notes: z.string().max(5000).nullable().optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: 'No fields to update' });

/**
 * Human warranty-vs-billable determination for a ticket (#301). Persists the
 * decision + an evidence snapshot so who decided (and why) is auditable and
 * ticket-billing can route accordingly.
 */
export const warrantyDeterminationSchema = z.object({
  cwTicketId: positiveIntId,
  decision: z.enum(WARRANTY_DECISIONS),
  coveringAgreementId: z.number().int().positive().optional(),
  coveringProjectId: z.number().int().positive().optional(),
  evidence: z.string().max(2000).optional(),
});

/**
 * PM task creation (#605). `status` is not accepted on create — new tasks start
 * `pending` (the Prisma default). `assignedTo` may be omitted or the literal
 * `'self'` (the route resolves either to the caller). `dueDate` must parse to a
 * real date so garbage never reaches `new Date(...)` → `Invalid Date` → a Prisma
 * 500. Validating `priority` against the enum keeps status-driven dashboards and
 * the assignment-alert severity honest.
 */
export const taskCreateSchema = z.object({
  title: z.string().min(1).max(500),
  description: z.string().max(5000).nullable().optional(),
  assignedTo: z.string().min(1).optional(),
  cwProjectId: z.coerce.number().int().positive().nullable().optional(),
  dueDate: validDateString.nullable().optional(),
  priority: z.enum(PM_TASK_PRIORITIES).optional(),
  // Schedule layer (#578): WBS parent + planned dates + effort + progress +
  // milestone flag. All optional/nullable so a minimal create (title only) still
  // succeeds and existing callers are unaffected. percentComplete is a 0–100 %.
  parentId: z.string().min(1).nullable().optional(),
  wbsCode: z.string().max(50).nullable().optional(),
  plannedStart: validDateString.nullable().optional(),
  plannedEnd: validDateString.nullable().optional(),
  estimatedHours: z.number().nonnegative().nullable().optional(),
  percentComplete: z.number().min(0).max(100).nullable().optional(),
  isMilestone: z.boolean().optional(),
});
export type TaskCreateInput = z.infer<typeof taskCreateSchema>;

/** Structured project meeting capture (#703). Action items become PMTask rows. */
export const projectMeetingCreateSchema = z.object({
  title: z.string().trim().min(1).max(300),
  occurredAt: validDateString,
  minutes: z.string().max(20_000).nullable().optional(),
  attendees: z.array(z.object({
    name: z.string().trim().min(1).max(200),
    email: z.string().trim().email().max(320).nullable().optional(),
  })).max(100).default([]),
  decisions: z.array(z.object({
    summary: z.string().trim().min(1).max(2_000),
    decidedBy: z.string().trim().max(200).nullable().optional(),
  })).max(100).default([]),
  actionItems: z.array(z.object({
    title: z.string().trim().min(1).max(500),
    description: z.string().max(5_000).nullable().optional(),
    assignedTo: z.string().trim().min(1).optional(),
    dueDate: validDateString.nullable().optional(),
    priority: z.enum(PM_TASK_PRIORITIES).optional(),
  })).max(100).default([]),
});
export type ProjectMeetingCreateInput = z.infer<typeof projectMeetingCreateSchema>;

/**
 * PM task update (#605). Every field optional (partial patch). `status` and
 * `priority` validate against the real enums so an arbitrary value can't break
 * the execution rollup / due-date nudges, and `dueDate` must be a real date (or
 * null to clear it).
 */
export const taskUpdateSchema = z.object({
  title: z.string().min(1).max(500).optional(),
  description: z.string().max(5000).nullable().optional(),
  status: z.enum(PM_TASK_STATUSES).optional(),
  priority: z.enum(PM_TASK_PRIORITIES).optional(),
  dueDate: validDateString.nullable().optional(),
  // Schedule layer (#578) — same fields as create, all optional (partial patch).
  parentId: z.string().min(1).nullable().optional(),
  wbsCode: z.string().max(50).nullable().optional(),
  plannedStart: validDateString.nullable().optional(),
  plannedEnd: validDateString.nullable().optional(),
  estimatedHours: z.number().nonnegative().nullable().optional(),
  percentComplete: z.number().min(0).max(100).nullable().optional(),
  isMilestone: z.boolean().optional(),
});
export type TaskUpdateInput = z.infer<typeof taskUpdateSchema>;

/**
 * Task dependency creation (#578). The successor is the `:id` route param; the
 * body names the predecessor plus the relation type + lag. `type` validates
 * against the four PDM types (defaults FS); `lagDays` is a whole-day lag (may be
 * negative for lead). The route rejects a self-dep, a duplicate, and a cycle.
 */
export const taskDependencyCreateSchema = z.object({
  predecessorId: z.string().min(1),
  type: z.enum(TASK_DEPENDENCY_TYPES).optional(),
  lagDays: z.number().int().optional(),
});
export type TaskDependencyCreateInput = z.infer<typeof taskDependencyCreateSchema>;

/**
 * RAID log item creation (#577). One schema for all four types (risk / issue /
 * assumption / decision) — the `type` discriminator selects which extra fields
 * apply. `status` is not accepted on create; new items start `open` (the Prisma
 * default). The type-specific fields (likelihood/impact/dueDate/decidedAt) are
 * always OPTIONAL so a minimal create (type + title) succeeds; the route only
 * persists the fields that belong to the given type, ignoring the rest. Dates
 * must parse to a real date so garbage never reaches `new Date(...)`.
 */
export const raidItemCreateSchema = z.object({
  type: z.enum(RAID_ITEM_TYPES),
  title: z.string().min(1).max(500),
  description: z.string().max(5000).nullable().optional(),
  owner: z.string().max(200).nullable().optional(),
  ownerId: z.string().max(200).nullable().optional(),
  // Risk-only qualifiers.
  likelihood: z.enum(RAID_LIKELIHOODS).nullable().optional(),
  impact: z.enum(RAID_IMPACTS).nullable().optional(),
  // Issue-only.
  dueDate: validDateString.nullable().optional(),
  // Decision-only.
  decidedAt: validDateString.nullable().optional(),
});
export type RaidItemCreateInput = z.infer<typeof raidItemCreateSchema>;

/**
 * RAID log item update (#577). Every field optional (partial patch); the body
 * must set at least one so an empty PATCH is a 400 rather than a silent no-op.
 * `type` is intentionally NOT updatable — an item's discriminator is fixed at
 * creation (changing a risk into a decision would orphan its likelihood/impact).
 * `status` validates against the shared RAID lifecycle enum.
 */
export const raidItemUpdateSchema = z
  .object({
    title: z.string().min(1).max(500).optional(),
    description: z.string().max(5000).nullable().optional(),
    status: z.enum(RAID_STATUSES).optional(),
    owner: z.string().max(200).nullable().optional(),
    ownerId: z.string().max(200).nullable().optional(),
    likelihood: z.enum(RAID_LIKELIHOODS).nullable().optional(),
    impact: z.enum(RAID_IMPACTS).nullable().optional(),
    dueDate: validDateString.nullable().optional(),
    decidedAt: validDateString.nullable().optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: 'No fields to update' });
export type RaidItemUpdateInput = z.infer<typeof raidItemUpdateSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Project milestones / stage gates (#654)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Create a project milestone (#654). `name` is required; a milestone with no
 * targetDate is a still-unscheduled gate (never overdue). `gateCriteria` is the
 * ADVISORY entry/exit criterion — free text, warn-only in v1. `status` defaults
 * to `pending` on the server, so a minimal create is just `{ name }`. Dates must
 * parse to real dates so garbage never reaches `new Date(...)`.
 */
export const milestoneCreateSchema = z.object({
  name: z.string().min(1).max(300),
  targetDate: validDateString.nullable().optional(),
  actualDate: validDateString.nullable().optional(),
  ownerId: z.string().max(200).nullable().optional(),
  ownerName: z.string().max(200).nullable().optional(),
  status: z.enum(MILESTONE_STATUSES).optional(),
  gateCriteria: z.string().max(5000).nullable().optional(),
  sortOrder: z.number().int().optional(),
});
export type MilestoneCreateInput = z.infer<typeof milestoneCreateSchema>;

/**
 * Update a project milestone (#654). Every field optional (partial patch); the
 * body must set at least one so an empty PATCH is a 400 rather than a silent
 * no-op. `status` validates against the shared lifecycle enum.
 */
export const milestoneUpdateSchema = z
  .object({
    name: z.string().min(1).max(300).optional(),
    targetDate: validDateString.nullable().optional(),
    actualDate: validDateString.nullable().optional(),
    ownerId: z.string().max(200).nullable().optional(),
    ownerName: z.string().max(200).nullable().optional(),
    status: z.enum(MILESTONE_STATUSES).optional(),
    gateCriteria: z.string().max(5000).nullable().optional(),
    sortOrder: z.number().int().optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: 'No fields to update' });
export type MilestoneUpdateInput = z.infer<typeof milestoneUpdateSchema>;

/**
 * Milestone template (#654) — a reusable, per-projectType set of stage-gate
 * milestones seeded onto a project. Mirrors PMTaskTemplate: a named template
 * scoped to a `projectType`, with ordered items. `offsetDays` lets a template
 * suggest a target date relative to the seed date (e.g. +30 = 30 days out); null
 * leaves the seeded milestone unscheduled.
 */
export const milestoneTemplateSchema = z.object({
  name: z.string().min(1).max(300),
  description: z.string().max(2000).optional(),
  projectType: z.string().max(200).optional(),
  items: z
    .array(
      z.object({
        name: z.string().min(1).max(300),
        gateCriteria: z.string().max(5000).optional(),
        offsetDays: z.number().int().nullable().optional(),
        sortOrder: z.number().int(),
      }),
    )
    .default([]),
});
export type MilestoneTemplateInput = z.infer<typeof milestoneTemplateSchema>;

/** Milestone template edit (#654) — copy-on-write, full new definition. */
export const milestoneTemplateUpdateSchema = milestoneTemplateSchema;
export type MilestoneTemplateUpdateInput = z.infer<typeof milestoneTemplateUpdateSchema>;

/** Manual mark-invoiced override (#605). `invoicedAt` must be a real date. */
export const markInvoicedSchema = z.object({
  invoiceNumber: z.string().min(1).max(200),
  invoicedAt: validDateString,
  billedBy: z.string().min(1).max(200).optional(),
});

/**
 * Recall a billing request from the Mission Control queue
 * (HTS-PROJECT-PORTAL-28). The reason is optional free text forwarded to MC.
 */
export const billingRequestRecallSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
export type BillingRequestRecallInput = z.infer<typeof billingRequestRecallSchema>;

/** Pagination query params */
export const paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().positive().max(1000).default(100),
});

/** Company search query */
export const companySearchSchema = z.object({
  q: z.string().min(1).max(200),
});

/** Certificate generation request body */
export const certificateGenerateSchema = z.object({
  projectNumber: z.union([z.string(), z.number()]),
  projectName: z.string().min(1).max(200),
  customerName: z.string().min(1).max(200),
  customerContact: z.string().max(200).optional(),
  completionDate: z.string().min(1),
  pmName: z.string().min(1).max(200),
  outstandingWork: z.string().max(2000).optional(),
  products: z
    .array(
      z.object({
        description: z.string(),
        quantity: z.number(),
        price: z.number(),
        extPrice: z.number(),
        catalogItemName: z.string().optional(),
      }),
    )
    .optional(),
});

/** Warranty creation request body */
export const warrantyCreateSchema = z.object({
  customerId: positiveIntId,
  cwProjectId: positiveIntId,
  totalWarrantyAmount: z.number().nonnegative(),
  warrantyData: z.object({
    name: z.string().max(200).optional(),
    typeId: z.number().int().positive().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    noEndingDateFlag: z.boolean().optional(),
    billAmount: z.number().optional(),
    applicationUnits: z.string().optional(),
    applicationLimit: z.number().optional(),
    applicationUnlimitedFlag: z.boolean().optional(),
    internalNotes: z.string().max(5000).optional(),
    billingCycle: z.object({ id: z.number() }).optional(),
    billTime: z.string().optional(),
    billExpenses: z.string().optional(),
    billProducts: z.string().optional(),
    siteId: z.number().int().positive().optional(),
    contactId: z.number().int().positive().optional(),
    companyLocationId: z.number().int().positive().optional(),
    billingCompanyId: z.number().int().positive().optional(),
    billingSiteId: z.number().int().positive().optional(),
    billingContactId: z.number().int().positive().optional(),
    shippingCompanyId: z.number().int().positive().optional(),
    shippingSiteId: z.number().int().positive().optional(),
    shippingContactId: z.number().int().positive().optional(),
    subContractorCompanyId: z.number().int().positive().optional(),
    opportunityId: z.number().int().positive().optional(),
    workRole: z.object({ id: z.number() }).nullable().optional(),
    workType: z.object({ id: z.number() }).nullable().optional(),
    attachments: z
      .array(z.object({ id: z.number(), fileName: z.string() }))
      .optional(),
  }),
});

/** Device import request body */
export const deviceImportSchema = z.object({
  devices: z.array(
    z.object({
      name: z.string(),
      model: z.string(),
      serialNumber: z.string(),
      status: z.string(),
    }).passthrough(),
  ),
  companyId: positiveIntId,
  siteId: z.number().int().positive().optional(),
  contactId: z.number().int().positive().optional(),
  typeId: positiveIntId,
  statusId: z.number().int().positive().optional(),
  manufacturerId: z.number().int().positive().optional(),
});

/** Checklist template creation */
export const checklistTemplateSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  projectType: z.string().max(100).optional(),
  items: z.array(
    z.object({
      question: z.string().min(1).max(500),
      itemType: z.enum(['boolean', 'text', 'photo', 'number']).default('boolean'),
      sortOrder: z.number().int().min(0),
      isRequired: z.boolean().default(true),
    }),
  ),
});

/**
 * Checklist template edit (#655) — copy-on-write. The body is the full new
 * definition (same shape as create); PATCH mints a NEW incremented-version row
 * from it and retires the current one, so partial patches are intentionally not
 * supported (a version is a complete, immutable snapshot).
 */
export const checklistTemplateUpdateSchema = checklistTemplateSchema;
export type ChecklistTemplateUpdateInput = z.infer<typeof checklistTemplateUpdateSchema>;

/** Kickoff plan import — tasks + checklist refs to seed a project */
export const planImportSchema = z.object({
  tasks: z.array(
    z.object({
      title: z.string().min(1),
      description: z.string().optional(),
      priority: z.string().optional(),
      phase: z.string().optional(),
      sortOrder: z.number().int().optional(),
      // Schedule layer (#578): a plan may seed schedule fields on its tasks.
      // All optional/nullable-safe so a plain title-only task still imports.
      wbsCode: z.string().max(50).optional(),
      plannedStart: validDateString.optional(),
      plannedEnd: validDateString.optional(),
      estimatedHours: z.number().nonnegative().optional(),
      isMilestone: z.boolean().optional(),
    }),
  ).default([]),
  checklists: z.array(
    z.object({
      templateId: z.string().optional(),
      templateName: z.string().optional(),
    }).refine((c) => c.templateId || c.templateName, 'templateId or templateName required'),
  ).default([]),
});
export type PlanImport = z.infer<typeof planImportSchema>;

/** PM task template — reusable set of tasks seeded onto a project at kickoff */
export const pmTaskTemplateSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  projectType: z.string().optional(),
  items: z.array(
    z.object({
      title: z.string().min(1),
      description: z.string().optional(),
      priority: z.string().default('normal'),
      phase: z.string().optional(),
      sortOrder: z.number().int(),
      isRequired: z.boolean().default(true),
    }),
  ).default([]),
});
export type PmTaskTemplateInput = z.infer<typeof pmTaskTemplateSchema>;

/** PM task template edit (#655) — copy-on-write, full new definition (see checklist). */
export const pmTaskTemplateUpdateSchema = pmTaskTemplateSchema;
export type PmTaskTemplateUpdateInput = z.infer<typeof pmTaskTemplateUpdateSchema>;

/** Kickoff project type selection — drives which task template + checklist get seeded */
export const kickoffProjectTypeSchema = z.object({ projectType: z.string().min(1) });
export type KickoffProjectTypeInput = z.infer<typeof kickoffProjectTypeSchema>;

/** Checklist instance creation */
export const checklistInstanceSchema = z.object({
  templateId: z.string().min(1),
  cwProjectId: z.number().int().positive().optional(),
  cwTicketId: z.number().int().positive().optional(),
  assignedTo: z.string().optional(),
});

/** Checklist response submission */
export const checklistResponseSchema = z.object({
  responses: z.array(
    z.object({
      templateItemId: z.string().min(1),
      value: z.string().optional(),
      photoUrl: z.string().url().optional(),
    }),
  ),
});

/** Dashboard preferences */
export const dashboardPreferencesSchema = z.object({
  widgets: z.array(
    z.object({
      id: z.string(),
      position: z.object({
        x: z.number(),
        y: z.number(),
        w: z.number(),
        h: z.number(),
      }),
    }),
  ),
  pinnedProjects: z.array(z.number()).optional(),
});

/** User creation (for subcontractors) */
export const userCreateSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(200),
  role: z.enum(['technician', 'tech_manager', 'project_manager', 'program_manager', 'subcontractor', 'sales', 'ops_leadership', 'warehouse_manager', 'org_admin']),
  companyName: z.string().max(200).optional(),
});

/** Time entry creation */
export const timeEntryCreateSchema = z.object({
  ticketId: z.number().int().positive(),
  hours: z.number().min(0.25).max(24),
  notes: z.string().max(2000).optional(),
  workTypeId: z.number().int().positive().optional(),
  timeStart: z.string().optional(),
  chargeToType: z.enum(['ProjectTicket', 'ServiceTicket']).optional(),
});

/** Time entry update */
export const timeEntryUpdateSchema = z.object({
  hours: z.number().min(0.25).max(24).optional(),
  notes: z.string().max(2000).optional(),
});

/** Portal setting update */
export const portalSettingSchema = z.object({
  value: z.unknown(),
});

/** Kickoff workflow initiation */
export const kickoffInitiateSchema = z.object({
  cwProjectId: positiveIntId,
  cwOpportunityId: z.number().int().positive().optional(),
  cwCompanyId: z.number().int().positive().optional(),
  companyName: z.string().max(200).optional(),
  projectName: z.string().max(200).optional(),
});

/** SOW save (edited text) */
export const kickoffSaveSowSchema = z.object({
  editedSowText: z.string().max(50000),
});

/** Schedule handoff call */
export const kickoffScheduleHandoffSchema = z.object({
  startTime: z.string().min(1),
  endTime: z.string().min(1),
  attendees: z.array(z.string().email()).min(1),
  optionalAttendees: z.array(z.string().email()).optional(),
  notes: z.string().max(5000).optional(),
  sharepointFolderUrl: z.string().max(1000).optional(),
});

/**
 * Find handoff free-busy slots (browser comment on Step 4 of the kickoff wizard).
 * Returns 5 business days of 30-min slots in working hours, with each slot
 * marked free / partial based on the HTS-attendee union of M365 calendars.
 */
export const kickoffFindHandoffSlotsSchema = z.object({
  attendees: z.array(z.string().email()).min(1),
  /** YYYY-MM-DD (ET). Snaps to the Monday of its week. Defaults to today. */
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'startDate must be YYYY-MM-DD')
    .optional(),
  /** Granularity in minutes. Default 30. */
  slotMinutes: z.number().int().positive().max(60).optional(),
  /** Working hours in ET, 24h. Default 9-17. */
  workingHours: z
    .object({
      start: z.number().int().min(0).max(23),
      end: z.number().int().min(1).max(24),
    })
    .refine((v) => v.end > v.start, { message: 'workingHours.end must be > start' })
    .optional(),
});

/** Tag an attachment */
export const kickoffTagAttachmentSchema = z.object({
  cwDocumentId: positiveIntId,
  tag: z.enum(['floorplan', 'sow', 'photo', 'general']),
  fileName: z.string().max(500).optional(),
});

/** Project status update */
export const projectStatusUpdateSchema = z.object({
  statusId: z.number().int().positive(),
});

/** Budget calculate with optional sub contractor cost */
export const budgetCalculateSchema = z.object({
  subContractorCost: z.number().min(0).optional().default(0),
});

/** Budget update (post-kickoff adjustment) */
export const budgetUpdateSchema = z.object({
  subContractorCost: z.number().min(0),
});

/** Billing request creation (project or service ticket) */
export const billingRequestCreateSchema = z.discriminatedUnion('requestType', [
  z.object({
    requestType: z.literal('project'),
    cwProjectId: z.number().int().positive(),
    amount: z.number().positive(),
    percentComplete: z.number().min(0).max(100).optional(),
    requestedPercent: z.number().min(0).max(100).optional(),
    billingType: z.enum(['labor', 'equipment']).optional(),
    description: z.string().max(500).optional(),
  }),
  z.object({
    requestType: z.literal('service_ticket'),
    cwTicketId: z.number().int().positive(),
    description: z.string().max(500).optional(),
  }),
]);

/** Billing request review (approve/reject by ops leadership) */
export const billingRequestReviewSchema = z.object({
  action: z.enum(['approve', 'reject']),
  adjustedAmount: z.number().positive().optional(),
  notes: z.string().max(500).optional(),
}).refine(
  (d) => d.action !== 'reject' || (d.notes && d.notes.length > 0),
  { message: 'Notes required when rejecting' },
).refine(
  (d) => !d.adjustedAmount || (d.notes && d.notes.length > 0),
  { message: 'Notes required when adjusting amount' },
);

/** MC webhook callback for billing request */
export const billingWebhookSchema = z.object({
  billingRequestId: z.string().min(1),
  invoiceNumber: z.string().min(1),
  invoicedAt: z.string().datetime(),
  billedBy: z.string().min(1),
});

/** Purchase request creation */
export const purchaseRequestSchema = z.object({
  cwProjectId: z.number().int().positive(),
  category: z.enum(['equipment', 'subcontractor']),
  amount: z.number().positive(),
  vendorName: z.string().max(200).optional(),
  description: z.string().min(1).max(500),
  deploymentWorkflowId: z.string().optional(),
});

/** Purchase request review */
export const purchaseRequestReviewSchema = z.object({
  action: z.enum(['approve', 'reject']),
  notes: z.string().max(500).optional(),
});

/** Purchasing limits admin settings */
export const purchasingLimitsSchema = z.object({
  equipment: z.object({
    overageThresholdDollars: z.number().min(0),
    overageThresholdPercent: z.number().min(0).max(100),
    mode: z.enum(['either', 'dollar', 'percent']),
    requireApprovalAboveDollars: z.number().min(0).default(0),
  }),
  subcontractor: z.object({
    overageThresholdDollars: z.number().min(0),
    overageThresholdPercent: z.number().min(0).max(100),
    mode: z.enum(['either', 'dollar', 'percent']),
    requireApprovalAboveDollars: z.number().min(0).default(0),
  }),
});

/** ConnectWise webhook callback body */
export const cwWebhookCallbackSchema = z.object({
  ID: z.number().int(),
  Type: z.string(),
  Action: z.string(),
  MemberID: z.string().optional(),
  // zod v4 requires explicit key type for z.record
  Entity: z.record(z.string(), z.unknown()).optional(),
});

/** Subcontractor onboarding license entry */
export const onboardingLicenseSchema = z.object({
  type: z.string().min(1).max(100),
  number: z.string().min(1).max(100),
  state: z.string().min(2).max(2),
  expiresAt: z.string().min(1),
});

/** Subcontractor onboarding submission */
export const subOnboardingSubmitSchema = z.object({
  // Company
  companyName: z.string().min(1).max(200),
  addressLine1: z.string().min(1).max(200),
  addressLine2: z.string().max(200).optional(),
  city: z.string().min(1).max(100),
  state: z.string().min(2).max(2),
  zip: z.string().min(5).max(10),
  phone: z.string().min(10).max(20),
  website: z.string().max(200).optional(),
  einTaxId: z.string().min(9).max(11),

  // Contact
  contactFirstName: z.string().min(1).max(100),
  contactLastName: z.string().min(1).max(100),
  contactEmail: z.string().email().max(200),
  contactPhone: z.string().min(10).max(20),
  contactTitle: z.string().max(100).optional(),

  // Authorized signer (optional — only when different from the primary contact)
  hasAlternativeSigner: z.coerce.boolean().default(false),
  signerFirstName: z.string().max(100).optional(),
  signerLastName: z.string().max(100).optional(),
  signerEmail: z.string().email().max(200).optional(),
  signerTitle: z.string().max(100).optional(),

  // Skills & coverage
  skills: z.array(z.string()).min(1),
  serviceRadiusMi: z.coerce.number().min(1).max(5000).default(50),
  isNationalBroker: z.coerce.boolean().default(false),

  // Licenses
  licenses: z.array(onboardingLicenseSchema).optional(),

  // Negotiation is dark-launched by the API; accepting the shape here is inert
  // while FF_MSA_NEGOTIATION is off.
  requestedTerms: RequestedTermsSchema.optional(),
  // MUL-3919 (Phase 3, §3.1): the §3.1 400 `{ error: 'request_reason_required' }`
  // branch requires the schema to accept ANY string (including the empty
  // string) so a missing-but-not-required field cannot surface as a generic
  // zod 400. The route handler enforces the 10–1000-char trimmed length
  // AFTER eligibility has been computed — a sub on the deny-list skips the
  // field and falls through to the standard flow silently.
  requestReason: z.string().max(2000).optional(),

  // ⛔ Insurance expiry dates are NO LONGER accepted from the client and are not
  // persisted from here. They are read off the certificate by the parser and
  // returned inside `coiAnalysisToken`, which is signed server-side; the submit
  // handler writes `confirmedCoi.dates` and ignores anything sent under these
  // names. Asking a subcontractor to type dates off their own certificate, with
  // nothing checking the typed value against the document, is what produced the
  // UNEVIDENCED class — seven coverage dates had to be cleared from production on
  // 2026-08-24 because no certificate evidenced any of them.
  //
  // Kept only so an older cached copy of the form does not fail validation
  // outright mid-submission. Remove once no client sends them. Do NOT wire these
  // back into the create call: that reopens the hole.
  glExpiresAt: z.string().optional(),
  autoExpiresAt: z.string().optional(),
  wcExpiresAt: z.string().optional(),
  umbrellaExpiresAt: z.string().optional(),

  // Opaque, server-signed result of the phase-one COI upload/parser pass. HS256
  // over JWT_SECRET, 1h expiry, bound to contactEmail, `algorithms` pinned on
  // verify. The dates live in here rather than in the request body precisely so a
  // subcontractor cannot assert their own expiry dates.
  coiAnalysisToken: z.string().min(1).optional(),
  coiConfirmed: z.boolean().optional(),

  // ACH/Banking (collected but NOT stored in DB — only in generated PDF)
  paymentMethod: z.enum(['ach', 'check']).default('ach'),
  achVendorName: z.string().max(200).optional(),
  achAddressLine1: z.string().max(200).optional(),
  achAddressLine2: z.string().max(200).optional(),
  achCity: z.string().max(100).optional(),
  achState: z.string().max(50).optional(),
  achZip: z.string().max(20).optional(),
  achBankName: z.string().max(200).optional(),
  achRoutingNumber: z.string().min(9).max(9).optional(),
  achAccountNumber: z.string().min(4).max(17).optional(),
  achAccountType: z.enum(['checking', 'savings']).optional(),
}).superRefine((val, ctx) => {
  // When an alternative authorized signer is named, their name + email are required.
  if (val.hasAlternativeSigner) {
    if (!val.signerFirstName) ctx.addIssue({ code: 'custom', path: ['signerFirstName'], message: 'Signer first name is required' });
    if (!val.signerLastName) ctx.addIssue({ code: 'custom', path: ['signerLastName'], message: 'Signer last name is required' });
    if (!val.signerEmail) ctx.addIssue({ code: 'custom', path: ['signerEmail'], message: 'Signer email is required' });
  }
  if (val.paymentMethod !== 'ach') return;
  const required: Array<string> = [
    'achVendorName', 'achAddressLine1', 'achCity', 'achState', 'achZip',
    'achBankName', 'achRoutingNumber', 'achAccountNumber', 'achAccountType',
  ];
  for (const k of required) {
    if (!(val as Record<string, unknown>)[k]) ctx.addIssue({ code: 'custom', path: [k], message: 'Required for ACH' });
  }
  // Enforce 2-char state abbreviation for ACH submissions
  const stateValue = (val as Record<string, unknown>).achState;
  if (typeof stateValue === 'string' && stateValue.length !== 2) {
    ctx.addIssue({
      code: 'custom',
      path: ['achState'],
      message: 'ACH state must be a 2-character US state abbreviation (e.g. CA)',
    });
  }
});

/** Admin review action */
export const subOnboardingReviewSchema = z.object({
  action: z.enum(['approve', 'reject']),
  notes: z.string().max(2000).optional(),
  // #296: default TRUE so an approved sub gets a portal login unless a reviewer
  // explicitly opts out — the old optional/undefined default silently dead-ended
  // approved subs who could never log in.
  createPortalUser: z.boolean().optional().default(true),
});

/** Deployment — add contacts to workflow */
export const deploymentAddContactsSchema = z.object({
  contacts: z.array(z.object({
    cwContactId: z.number().int().positive(),
    // CW-sourced data: names arrive padded and last names can be blank — trim on
    // the way in and accept a blank lastName instead of failing the whole batch
    // (Sentry 2026-08-24: two company contacts with blank lastNames made every
    // "Add Selected" attempt 500 for the user).
    firstName: z.string().trim().min(1).max(100),
    lastName: z.string().trim().max(100),
    email: z.string().email().max(200).nullish(),
    phone: z.string().max(20).nullish(),
    isPrimary: z.boolean().optional(),
  })).min(1),
});

/** Deployment — create a new CW contact and add to workflow */
export const deploymentCreateContactSchema = z.object({
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  email: z.string().email().max(200),
  phone: z.string().max(20).optional(),
  title: z.string().max(100).optional(),
});

/** Deployment — schedule customer kickoff meeting */
export const deploymentScheduleKickoffSchema = z.object({
  startTime: z.string().min(1),
  endTime: z.string().min(1),
  attendees: z.array(z.string().email()).min(1),
  optionalAttendees: z.array(z.string().email()).optional(),
  notes: z.string().max(5000).optional(),
});

/** Deployment — complete kickoff */
export const deploymentCompleteKickoffSchema = z.object({
  notes: z.string().max(5000).optional(),
});

// ---------------------------------------------------------------------------
// Time Clock schemas
// ---------------------------------------------------------------------------

/** Clock-in request */
export const clockInSchema = z.object({
  cwTicketId: z.number().int().positive().optional(),
  ticketType: z.enum(['ServiceTicket', 'ProjectTicket']).optional(),
  chargeToType: z.enum(['ServiceTicket', 'ProjectTicket', 'Company', 'ChargeCode']).optional(),
  cwCompanyId: z.number().int().positive().optional(),
  cwChargeCodeId: z.number().int().positive().optional(),
  chargeCodeName: z.string().max(200).optional(),
  workTypeId: z.number().int().positive().optional(),
  workTypeName: z.string().max(100).optional(),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  workState: z.string().min(2).max(2).optional(),
  notes: z.string().max(2000).optional(),
}).refine(
  (d) => d.cwTicketId || d.chargeToType === 'Company' || (d.chargeToType === 'ChargeCode' && d.cwChargeCodeId),
  { message: 'Either cwTicketId, chargeToType=Company, or chargeToType=ChargeCode with cwChargeCodeId is required' },
);

/** Clock-out request */
export const clockOutSchema = z.object({
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  notes: z.string().max(2000).optional(),
  workTypeId: z.number().int().positive().optional(),
  workRoleId: z.number().int().positive().optional(),
  acknowledgeBreakViolation: z.boolean().optional(),
});

/** Break start request */
export const breakStartSchema = z.object({
  breakType: z.enum(['meal', 'rest', 'other']).default('meal'),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
});

/** Break end request */
export const breakEndSchema = z.object({
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
});

/** Edit a completed clock session.
 *
 *  Time components are split into drive / work / break minutes so techs can
 *  correct each one independently. The server recomputes the underlying
 *  timestamps (arrivedAt, clockOutAt, driveLeg.endAt) and rescales the
 *  existing break list to keep the total break time consistent.
 *
 *  `note` is a free-text explanation of WHY the change was made. It's written
 *  to the timeClock audit log for payroll/invoicing review, but it is NOT
 *  required — the field page modal encourages it but lets the tech submit
 *  without one (the audit row will simply have an empty note in that case). */
export const clockEditSchema = z.object({
  // Time components — any combination may be supplied; missing components
  // are left untouched. Bounds are generous: a 12h visit is the practical
  // ceiling, but we allow 24h to handle overnight / on-call edge cases.
  driveMinutes: z.number().min(0).max(720).optional(),
  workMinutes: z.number().min(0).max(1440).optional(),
  breakMinutes: z.number().min(0).max(720).optional(),
  // Legacy fields — kept for backward-compat with the older "single hours"
  // modal. If actualHours is supplied without the new components, the server
  // treats it as "adjust total on-site time, leave drive/breaks alone".
  actualHours: z.number().min(0.25).max(24).optional(),
  notes: z.string().max(2000).optional(),
  // Audit note — the only "why" field. Optional but recommended; tracked
  // in the timeClock audit log on every edit.
  note: z.string().max(500).optional(),
});

/** Log travel time as separate CW time entry */
export const travelLogSchema = z.object({
  timeClockId: z.string().min(1),
  durationMin: z.number().min(1).max(480),
  notes: z.string().max(2000).optional(),
  workTypeId: z.number().int().positive().optional(),
});

// ---------------------------------------------------------------------------
// Work Day schemas (tech daily workflow)
// ---------------------------------------------------------------------------

/** GPS coordinates — reusable fragment */
const gpsCoords = {
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
};

/** Start a work day — tech taps "Enroute" on first dispatch */
export const workDayStartSchema = z.object({
  cwTicketId: z.number().int().positive(),
  ticketType: z.enum(['ServiceTicket', 'ProjectTicket']),
  ...gpsCoords,
});

/** Arrive on site — tech taps "Arrived" */
export const workDayArriveSchema = z.object({
  ...gpsCoords,
});

/** Cancel from enroute */
export const workDayCancelSchema = z.object({
  ...gpsCoords,
});

/** Complete a job — tech taps "Complete" */
export const workDayCompleteSchema = z.object({
  ...gpsCoords,
  notes: z.string().max(2000).optional(),
  workTypeId: z.number().int().positive().optional(),
  workRoleId: z.number().int().positive().optional(),
  acknowledgeBreakViolation: z.boolean().optional(),
});

/** Start next job — tech taps "Enroute" on next dispatch */
export const workDayNextJobSchema = z.object({
  cwTicketId: z.number().int().positive(),
  ticketType: z.enum(['ServiceTicket', 'ProjectTicket']),
  ...gpsCoords,
});

/** Depart home — tech taps "Depart Home" */
export const workDayDepartHomeSchema = z.object({
  ...gpsCoords,
});

/** End day — tech taps "End Day — I'm Home" */
export const workDayEndDaySchema = z.object({
  ...gpsCoords,
  acknowledgeBreakViolation: z.boolean().optional(),
});

/** Commute config — admin sets tech's office */
export const commuteConfigSchema = z.object({
  officeAddress: z.string().min(1).max(500),
  officeLat: z.number().min(-90).max(90),
  officeLng: z.number().min(-180).max(180),
  commuteMinutes: z.number().int().min(0).max(180),
  commuteDistanceMi: z.number().min(0).max(500).optional(),
});

/** Board status mapping — admin maps CW board statuses */
export const boardStatusMappingSchema = z.object({
  // #625: which CW id namespace this mapping belongs to. Service and project
  // boards can share a numeric id, so a mapping is scoped to (boardId, boardType).
  // Defaults to 'service' — the historical admin list only surfaced service boards.
  boardType: z.enum(['service', 'project']).default('service'),
  boardName: z.string().min(1).max(200),
  enrouteStatusId: z.number().int().positive().nullable(),
  enrouteStatusName: z.string().max(200).nullable(),
  completeStatusId: z.number().int().positive().nullable(),
  completeStatusName: z.string().max(200).nullable(),
  cancelStatusId: z.number().int().positive().nullable(),
  cancelStatusName: z.string().max(200).nullable(),
});

/** One itemized line as printed on the invoice document. */
export const invoiceLineItemSchema = z.object({
  description: z.string().min(1).max(200),
  amount: z.number().positive(),
});

/** What the document reader extracted — echoed at submit so reviewers can
 *  compare the read against what the sub actually filed (advisory evidence). */
export const invoiceOcrReadSchema = z.object({
  status: z.enum(['ok', 'unavailable', 'failed']),
  invoiceNumber: z.string().max(100).nullable().optional(),
  amount: z.number().positive().nullable().optional(),
  invoiceDate: z.string().max(10).nullable().optional(),
  dueDate: z.string().max(10).nullable().optional(),
  poNumber: z.string().max(100).nullable().optional(),
  vendorName: z.string().max(200).nullable().optional(),
  tax: z.number().min(0).nullable().optional(),
  lineItems: z.array(invoiceLineItemSchema).max(30).optional(),
  lowConfidenceFields: z.array(z.string().max(50)).max(10).optional(),
  fieldConfidence: z.record(z.string().max(50), z.number().min(0).max(1)).optional(),
});

/** Sub invoice creation (partner portal) */
export const subInvoiceCreateSchema = z.object({
  poNumber: z.string().min(1),
  invoiceNumber: z.string().min(1).max(100),
  amount: z.number().positive(),
  description: z.string().max(2000).optional(),
  invoiceDate: z.string().optional(),
  ocrRead: invoiceOcrReadSchema.optional(),
});

/** Sub invoice review (PM/Ops approval). `verified` is REQUIRED on the final
 *  (ops) approval — the reviewer confirmed the document matches the filed
 *  fields in the verification panel before approving. */
export const subInvoiceReviewSchema = z.object({
  action: z.enum(['approve', 'reject']),
  notes: z.string().max(2000).optional(),
  verified: z.boolean().optional(),
});

/** Reviewer correction of the filed values before approval (the review
 *  drawer's proofing form). At least one field must change; the reason is the
 *  audit trail. The PO itself is not editable — wrong PO is a rejection
 *  ("Not for this PO"), not a correction, because it moves dollars to a
 *  different PO without the submit-side ownership/cap checks. Line items and
 *  due date are part of the durable verified-detail backup. */
export const subInvoiceFieldCorrectionSchema = z.object({
  amount: z.number().positive().optional(),
  invoiceNumber: z.string().min(1).max(100).optional(),
  invoiceDate: z.string().min(4).max(10).nullable().optional(),
  dueDate: z.string().min(4).max(10).nullable().optional(),
  description: z.string().max(2000).optional(),
  lineItems: z.array(invoiceLineItemSchema).max(30).optional(),
  /** Re-pointing the invoice at a different PO (2026-09-12: humans correct
   *  everything). Money-moving: the handler re-validates vendor ownership,
   *  open status, and the over-invoice cap against the NEW PO before applying. */
  poNumber: z.string().min(1).max(100).optional(),
  /** The vendor label as it should read (display + mirror row). */
  vendorName: z.string().min(1).max(200).optional(),
  /** Total tax (sales/use) as verified — rides the durable detail snapshot. */
  tax: z.number().min(0).optional(),
  reason: z.string().min(3).max(500),
}).strict().refine(
  (body) => body.amount !== undefined || body.invoiceNumber !== undefined
    || body.invoiceDate !== undefined || body.dueDate !== undefined
    || body.description !== undefined || body.lineItems !== undefined
    || body.poNumber !== undefined || body.vendorName !== undefined
    || body.tax !== undefined,
  { message: 'At least one field to correct is required' },
);

/** Feedback submission */
export const feedbackCreateSchema = z.object({
  type: z.enum(['bug', 'feature']),
  title: z.string().min(3).max(200),
  description: z.string().min(10).max(5000),
  pageUrl: z.string().max(500).optional(),
  userAgent: z.string().max(500).optional(),
  appVersion: z.string().max(20).optional(),
  screenSize: z.string().max(20).optional(),
  consoleLogs: z.string().max(50000).optional(),
});

// ---------------------------------------------------------------------------
// Policy Management schemas
// ---------------------------------------------------------------------------

/** Create a new policy */
export const policyCreateSchema = z.object({
  title: z.string().min(1).max(300),
  slug: z.string().min(1).max(300).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be URL-safe (lowercase, hyphens only)'),
  category: z.enum(['health_safety', 'cyber_security', 'hr_employment', 'it_acceptable_use', 'operations', 'financial', 'legal_compliance']),
  requiresAck: z.boolean().optional().default(false),
  draftContent: z.string().optional(),
});

/** Update a policy draft */
export const policyUpdateSchema = z.object({
  title: z.string().min(1).max(300).optional(),
  slug: z.string().min(1).max(300).regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be URL-safe').optional(),
  category: z.enum(['health_safety', 'cyber_security', 'hr_employment', 'it_acceptable_use', 'operations', 'financial', 'legal_compliance']).optional(),
  requiresAck: z.boolean().optional(),
  draftTitle: z.string().max(300).optional(),
  draftContent: z.string().optional(),
});

/** Publish a policy */
export const policyPublishSchema = z.object({
  approverName: z.string().min(1).max(200),
  approverTitle: z.string().min(1).max(200),
});

/** Acknowledge a policy */
export const policyAcknowledgeSchema = z.object({
  version: z.number().int().positive(),
});

/** Create/update a policy bundle */
export const policyBundleSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(1000).optional(),
  policyIds: z.array(z.string()).min(1),
});

/** Export policies as PDF or ZIP */
export const policyExportSchema = z.object({
  policyIds: z.array(z.string()).min(1),
  format: z.enum(['pdf', 'zip']).default('pdf'),
});

/** Category visibility for subcontractors */
export const policyCategoryVisibilitySchema = z.object({
  visibility: z.array(z.object({
    category: z.enum(['health_safety', 'cyber_security', 'hr_employment', 'it_acceptable_use', 'operations', 'financial', 'legal_compliance']),
    visibleToSubcontractors: z.boolean(),
  })),
});

// ---------------------------------------------------------------------------
// Safety Reporting & ISNetworld Compliance schemas
// ---------------------------------------------------------------------------

/** Safety incident types */
export const safetyIncidentTypes = ['injury', 'illness', 'near_miss', 'property_damage', 'vehicle_accident', 'environmental'] as const;

/** Safety incident severity levels */
export const safetyIncidentSeverities = ['first_aid', 'recordable', 'lost_time', 'restricted_duty', 'fatality'] as const;

/** OSHA recordable classifications */
export const oshaClassifications = ['other_recordable', 'days_away', 'restricted_transfer', 'fatality'] as const;

/** Safety training categories */
export const safetyTrainingCategories = ['osha', 'cyber', 'fall_protection', 'hazcom', 'first_aid', 'cpr', 'forklift', 'electrical', 'ppe', 'driving', 'confined_space', 'lockout_tagout', 'other'] as const;

/** Create a safety incident */
export const safetyIncidentCreateSchema = z.object({
  incidentDate: z.string().min(1),
  type: z.enum(safetyIncidentTypes),
  severity: z.enum(safetyIncidentSeverities),
  oshaClassification: z.enum(oshaClassifications).optional(),
  description: z.string().min(10).max(10000),
  location: z.string().max(500).optional(),
  employeeId: z.string().optional(),
  employeeName: z.string().min(1).max(200),
  projectId: z.number().int().positive().optional(),
  projectName: z.string().max(200).optional(),
  daysAway: z.number().int().min(0).default(0),
  daysRestricted: z.number().int().min(0).default(0),
  isVehicleRelated: z.boolean().default(false),
});

/** Update a safety incident */
export const safetyIncidentUpdateSchema = z.object({
  type: z.enum(safetyIncidentTypes).optional(),
  severity: z.enum(safetyIncidentSeverities).optional(),
  oshaClassification: z.enum(oshaClassifications).nullable().optional(),
  description: z.string().min(10).max(10000).optional(),
  location: z.string().max(500).optional(),
  employeeName: z.string().min(1).max(200).optional(),
  daysAway: z.number().int().min(0).optional(),
  daysRestricted: z.number().int().min(0).optional(),
  isVehicleRelated: z.boolean().optional(),
  status: z.enum(['open', 'investigating', 'closed']).optional(),
  rootCause: z.string().max(10000).optional(),
  correctiveAction: z.string().max(10000).optional(),
});

/** Create a safety training record */
export const safetyTrainingCreateSchema = z.object({
  userId: z.string().min(1),
  userName: z.string().min(1).max(200),
  courseName: z.string().min(1).max(300),
  category: z.enum(safetyTrainingCategories),
  provider: z.string().max(200).optional(),
  completedDate: z.string().min(1),
  expiryDate: z.string().optional(),
  certificateUrl: z.string().url().max(2000).optional(),
});

/** Create/update a safety metrics snapshot */
export const safetyMetricsCreateSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  quarter: z.number().int().min(1).max(4).optional(),
  avgEmployeeCount: z.number().min(0),
  totalHoursWorked: z.number().min(0),
  fatalities: z.number().int().min(0).default(0),
  casesWithDaysAway: z.number().int().min(0).default(0),
  casesRestricted: z.number().int().min(0).default(0),
  otherRecordable: z.number().int().min(0).default(0),
  totalDaysAway: z.number().int().min(0).default(0),
  totalDaysRestricted: z.number().int().min(0).default(0),
  totalVehicleAccidents: z.number().int().min(0).default(0),
  totalMilesDriven: z.number().min(0).optional(),
});

/**
 * F-13 / MUL-3480: refuse to boot when DEV_BYPASS_ENABLED is combined with
 * an environment where the dev bypass must NEVER be active.
 *
 * Auth.ts and rate-limit.ts both key their dev-bypass behaviour off the same
 * two env flags (`NODE_ENV` and `DEV_BYPASS_ENABLED`) with no independent
 * boot-time assertion. A future production misconfig — say a forgotten
 * `DEV_BYPASS_ENABLED=true` left in an app-setting — would silently disable
 * authentication and every rate limiter in the API. The helper is the single
 * source of truth for the unsafe combination; `envSchema.refine()` calls it
 * at parse time and `apps/api/src/server.ts` calls it as an explicit startup
 * guard right next to the existing JWT_SECRET check.
 *
 * The Azure App Service marker check matters because `envSchema` below
 * defaults `NODE_ENV` to `'development'` when unset — a production-style
 * deploy without an explicit `NODE_ENV` would otherwise look like a
 * development environment and slip past the NODE_ENV-only check.
 *
 * Returns `true` when the combination is unsafe and the process must refuse
 * to boot. Returns `false` when the bypass is either disabled or safe.
 */
export function isDevBypassUnsafeForEnv(env: {
  NODE_ENV: string;
  DEV_BYPASS_ENABLED?: string;
  WEBSITE_SITE_NAME?: string;
  WEBSITE_INSTANCE_ID?: string;
}): boolean {
  if (env.DEV_BYPASS_ENABLED !== 'true') return false;
  if (env.NODE_ENV !== 'development') return true;
  if (env.WEBSITE_SITE_NAME || env.WEBSITE_INSTANCE_ID) return true;
  return false;
}

/** Environment variables validation */
export const envSchema = z.object({
  NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),
  PORT: z.coerce.number().default(3001),
  DATABASE_URL: z.string().min(1),
  JWT_SECRET: z.string().min(32),
  CONNECTWISE_COMPANY_ID: z.string().min(1),
  CONNECTWISE_PUBLIC_KEY: z.string().min(1),
  CONNECTWISE_PRIVATE_KEY: z.string().min(1),
  CONNECTWISE_CLIENT_ID: z.string().min(1),
  CONNECTWISE_API_URL: z.string().url().default('https://api-na.myconnectwise.net'),
  // The only origin allowed to embed /warehouse/pod. A wildcard subdomain is
  // supported because ConnectWise Manage tenant hosts vary by region/account.
  CW_POD_FRAME_ANCESTOR: z.string()
    .regex(/^https:\/\/(?:\*\.)?[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?(?::\d+)?$/i)
    .default('https://*.myconnectwise.net'),
  AZURE_TENANT_ID: z.string().min(1).optional(),
  AZURE_CLIENT_ID: z.string().min(1).optional(),
  AZURE_CLIENT_SECRET: z.string().min(1).optional(),
  AZURE_SPA_CLIENT_ID: z.string().min(1).optional(), // Frontend MSAL app registration client ID (for id_token validation)
  CW_DATABASE_URL: z.string().min(1).optional(),
  ANTHROPIC_API_KEY: z.string().min(1).optional(),
  GHE_TOKEN: z.string().min(1).optional(),
  CPQ_COMPANY_ID: z.string().min(1).optional(),
  CPQ_PUBLIC_KEY: z.string().min(1).optional(),
  CPQ_PRIVATE_KEY: z.string().min(1).optional(),
  CPQ_CLIENT_ID: z.string().min(1).optional(),
  CW_CALLBACK_SECRET: z.string().min(1).optional(),
  SHAREPOINT_SITE_ID: z.string().min(1).optional(),
  SHAREPOINT_DRIVE_ID: z.string().min(1).optional(),
  GOOGLE_MAPS_API_KEY: z.string().min(1).optional(),
  AZURE_STORAGE_CONNECTION_STRING: z.string().min(1).optional(),
  QBO_CLIENT_ID: z.string().min(1).optional(),
  QBO_CLIENT_SECRET: z.string().min(1).optional(),
  QBO_REDIRECT_URI: z.string().url().optional(),
  QBO_ENVIRONMENT: z.enum(['sandbox', 'production']).default('production').optional(),
  MC_API_URL: z.string().url().optional(),
  MC_API_KEY: z.string().min(16).optional(),
  // Dedicated read-only key for Mission Control governed reporting embeds.
  // Never reuse MC_API_KEY or expose this value to the browser.
  MC_REPORTING_API_KEY: z.string().min(16).optional(),
  MC_REPORTING_COMPANY_ID: z.coerce.number().int().positive().optional(),
  MC_FLEET_API_KEY: z.string().min(1).optional(),
  MC_BILLING_WEBHOOK_KEY: z.string().min(1).optional(),
  PORTAL_PURCHASING_SERVICE_TOKEN: z.string().min(16).optional(),
  PORTAL_PURCHASING_WEBHOOK_URL: z.string().url().optional(),
  PORTAL_PURCHASING_WEBHOOK_SECRET: z.string().min(16).optional(),
  PORTAL_PURCHASING_CW_WRITE_ENABLED: z.enum(['true', 'false']).default('false').optional(),
  PORTAL_PURCHASING_OPS_EMAIL: z.string().email().optional(),
  PORTAL_PURCHASING_EXEC_EMAIL: z.string().email().optional(),
  PORTAL_BASE_URL: z.string().url().optional(),
  AP_MAILBOX_EMAIL: z.string().email().optional(),
  // #MUL-2278: AR/AP billing mailbox bindings. The mailboxes are separate from the
  // existing AP procurement inbox (AP_MAILBOX_EMAIL) and the COI reply mailbox
  // (subonboarding@hts.pro). Each is exact-opt-in via === 'true' on its enabled flag
  // and slot-sticky; default OFF so a copied command cannot register either job.
  AR_BILLING_MAILBOX_EMAIL: z.string().email().optional(),
  AR_BILLING_MAILBOX_INGESTION_ENABLED: z.string().min(1).optional(),
  AP_BILLING_MAILBOX_EMAIL: z.string().email().optional(),
  AP_BILLING_MAILBOX_INGESTION_ENABLED: z.string().min(1).optional(),
  CW_DEFAULT_PO_TERMS_ID: z.coerce.number().int().positive().default(1),
  CW_DEFAULT_PO_WAREHOUSE_ID: z.coerce.number().int().positive().default(1),
  CW_SUBCONTRACT_LABOR_CATALOG_ID: z.coerce.number().int().positive().default(2232),
  // Background-job slot gating (#289). WEBSITE_SLOT_NAME is injected by Azure App
  // Service on every slot; ENABLE_JOBS is an optional slot-STICKY override.
  WEBSITE_SLOT_NAME: z.string().min(1).optional(),
  ENABLE_JOBS: z.string().min(1).optional(),
  // F-13 / MUL-3480: Azure App Service runtime markers. `WEBSITE_SITE_NAME` and
  // `WEBSITE_INSTANCE_ID` are set on every App Service instance regardless of
  // slot or NODE_ENV, so the dev-bypass safety refinement below uses them to
  // detect a production-style deploy without relying on NODE_ENV being set
  // explicitly. Declared here so the refinement can read them off the parsed
  // env object instead of process.env directly (keeps the helper pure and
  // unit-testable without stubbing globals).
  WEBSITE_SITE_NAME: z.string().min(1).optional(),
  WEBSITE_INSTANCE_ID: z.string().min(1).optional(),
  // Warehouse print bridge (#1014). Physical claiming is independently gated
  // and slot-sticky; the Ed25519 private key is supplied by Key Vault only.
  WAREHOUSE_PRINT_AGENT_ENABLED: z.string().min(1).optional(),
  WAREHOUSE_PRINT_SIGNING_PRIVATE_KEY: z.string().min(1).optional(),
  WAREHOUSE_PRINT_SIGNING_PUBLIC_KEY: z.string().min(1).optional(),
  // CW time-entry needs_sync reconcile (#556). Default OFF: the reconcile job runs
  // in SHADOW mode (logs which stranded rows it WOULD re-post) until this is set to
  // 'true'/'1', so it merges safely and we verify against Test Customer 19323 first.
  CW_SYNC_RETRY_ENABLED: z.string().min(1).optional(),
  // Optional ISO cutover widening which stranded rows the reconcile will AUTO-CREATE.
  // The dedup marker ships in this deploy, so rows created at/after the deploy carry
  // it and a CW-side absence is trustworthy. Defaults to this process's start time;
  // set this to the deploy timestamp to also cover rows stranded between deploy and a
  // later restart. Older ("pre-marker") rows are quarantined, never auto-created (#556).
  CW_SYNC_RETRY_MARKER_CUTOVER: z.string().min(1).optional(),
  // #616: instantiate portal plan tasks (PMTask) as real CW project tickets. Default
  // OFF ('true'/'1' to enable) so `instantiatePlan` behaves exactly as today (portal
  // rows only) until we verify against Test Customer 19323. Dedup is enforced per-task
  // (persisted PMTask.cwTicketId + a `[hts:pmtask=<id>]` summary marker), so enabling
  // it later safely backfills already-seeded projects without duplicating CW tickets.
  PLAN_CW_TICKETS_ENABLED: z.string().min(1).optional(),
  // Survey floorplan Task -> CW project-ticket sync (#777). Default OFF;
  // preview stays read-only and live apply has persisted-id + marker dedup.
  SURVEY_TASK_CW_TICKETS_ENABLED: z.string().min(1).optional(),
  // Account-activity CW note backfill (#610). Default OFF: the backfill job runs in
  // SHADOW mode (logs which un-mirrored activities it WOULD re-post to a CW company
  // note) until this is set to 'true'/'1', so it merges safely and can be verified
  // against Test Customer 19323 before it writes to ConnectWise. Notes are idempotent
  // per candidate (only cwNoteId=null rows, claim-once stamp), so re-posting is low-risk.
  ACCOUNT_ACTIVITY_BACKFILL_ENABLED: z.string().min(1).optional(),
  // Design-domain local-development flags (#945). DEV_BYPASS_ENABLED disables auth
  // middleware in the API for local integration testing; SEED_CAMERA_CATALOG triggers
  // seeding of the camera device-model catalog from the bundled JSON fixture.
  DEV_BYPASS_ENABLED: z.string().min(1).optional(),
  SEED_CAMERA_CATALOG: z.string().min(1).optional(),
  SEED_CAMERA_CATALOG_PATH: z.string().min(1).optional(),
  // Amazon Business punchout (MUL-2682). Default OFF: the whole /api/amazon module
  // 503s without AMAZON_PUNCHOUT_ENABLED. Credentials are Amazon Test-mode only until
  // the acceptance run; the two inbound cXML endpoints validate the cXML Sender
  // shared secret instead of JWT auth.
  AMAZON_PUNCHOUT_ENABLED: z.string().min(1).optional(),
  AMAZON_PUNCHOUT_IDENTITY: z.string().min(1).optional(),
  AMAZON_PUNCHOUT_SHARED_SECRET: z.string().min(1).optional(),
  AMAZON_PUNCHOUT_URL: z.string().url().optional(),
  AMAZON_PUNCHOUT_RETURN_URL: z.string().url().optional(),
  // Exact opt-in: unset or any value other than "false" keeps OrderRequest dry-run.
  AMAZON_ORDER_REQUEST_DRY_RUN: z.string().optional(),
  // CW vendor id for Amazon Business; when unset the vendor is resolved by
  // company-name search at order time.
  CW_AMAZON_VENDOR_ID: z.coerce.number().int().positive().optional(),
  // Bucket catalog identifiers (design §2) — looked up by identifier at runtime,
  // never hardcoded catalog ids.
  AMAZON_BUCKET_UNMAPPED_IDENT: z.string().min(1).default('AMZN-UNMAPPED'),
  AMAZON_BUCKET_CONSUMABLE_IDENT: z.string().min(1).default('AMZN-CONSUMABLE'),
  AMAZON_BUCKET_INTERNAL_IDENT: z.string().min(1).default('AMZN-INTERNAL'),
}).refine(
  (env) => !isDevBypassUnsafeForEnv(env),
  {
    message:
      'DEV_BYPASS_ENABLED=true is not allowed when NODE_ENV is not "development" ' +
      'or when an Azure App Service marker (WEBSITE_SITE_NAME / WEBSITE_INSTANCE_ID) ' +
      'is present. Remove DEV_BYPASS_ENABLED, or set NODE_ENV=development AND ensure ' +
      'no Azure App Service markers are set.',
  },
);

// ---------------------------------------------------------------------------
// Change Order schemas
// ---------------------------------------------------------------------------

/** Change order review (verify/reject/complete) */
export const changeOrderReviewSchema = z.object({
  action: z.enum(['verify', 'reject', 'complete']),
  notes: z.string().max(1000).optional(),
  scopeAdjustment: z.object({
    cwPhaseId: positiveIntId,
    scheduleShiftDays: z.number().int().min(0).max(365).optional().default(0),
  }).optional(),
}).refine(
  (d) => d.action !== 'reject' || (d.notes && d.notes.length > 0),
  { message: 'Notes are required when rejecting', path: ['notes'] },
).refine(
  (d) => d.action === 'complete' || d.scopeAdjustment === undefined,
  { message: 'Scope adjustment is only valid when completing', path: ['scopeAdjustment'] },
);

/** Change order sales order submission */
export const changeOrderSalesOrderSchema = z.object({
  salesOrderNumber: z.string().min(1).max(100),
  salesOrderAmount: z.number().positive(),
});

/** Change order CW project link */
export const changeOrderCwProjectSchema = z.object({
  cwChangeOrderProjectId: positiveIntId.nullable(),
});

/**
 * Change order customer-approval artifact (#657). Records who approved, when,
 * and — when no signed file is uploaded — a signed-doc / DocuSign-completed URL.
 * The URL is optional here because the route accepts EITHER a multipart file
 * upload (which produces the stored URL) OR this pasted URL; the route enforces
 * that at least one is present.
 */
export const changeOrderCustomerApprovalSchema = z.object({
  customerApproverName: z.string().min(1).max(200),
  customerApprovedAt: z.coerce.date(),
  customerApprovalUrl: z.string().url().max(2000).optional(),
});

// ---------------------------------------------------------------------------
// Ticket Billing schemas
// ---------------------------------------------------------------------------

/** Customer pricing — set default discount for a company */
export const customerPricingSchema = z.object({
  defaultDiscount: z.number().min(0).max(1).nullable(),
  companyName: z.string().min(1).max(200),
});

/** Update a product price on a ticket */
export const ticketProductPriceSchema = z.object({
  price: z.number().min(0),
});

/** Add sub labor to a service ticket */
export const ticketSubLaborSchema = z.object({
  vendorId: z.number().int().positive(),
  vendorName: z.string().min(1).max(200),
  amount: z.number().min(0),
  description: z.string().max(2000).optional(),
});

// ---------------------------------------------------------------------------
// Service Core schemas
// ---------------------------------------------------------------------------

/** Set a contracted rate override for a company+work role on a CW agreement */
export const contractedRateSchema = z.object({
  cwCompanyId: z.number().int().positive(),
  companyName: z.string().min(1),
  workRoleId: z.number().int().positive(),
  rate: z.number().nonnegative(),
  contactId: z.number().int().positive().optional(),
});

/** Convert sub-labor spend to a CW time entry */
export const subLaborConvertSchema = z.object({
  ticketType: z.enum(['ServiceTicket', 'ProjectTicket']),
  memberId: z.number().int().positive(),
  amount: z.number().positive(),
  customerRate: z.number().positive(),
  workTypeId: z.number().int().positive().optional(),
  workRoleId: z.number().int().positive().optional(),
  notes: z.string().optional(),
  // #276: when converting a specific TicketSubLabor row, pass its id so the
  // created (billable) CW time entry can be linked back to the row. Billing-
  // request math then counts those dollars once (on the labor side) instead of
  // twice (labor + the raw sub-labor row).
  subLaborId: z.string().optional(),
});

/** Set a single product price */
export const productPriceSchema = z.object({ price: z.number().nonnegative() });

/** Project dates update — all fields optional, at least one required */
export const projectDatesSchema = z.object({
  estimatedStart: z.string().datetime().optional(),
  estimatedEnd: z.string().datetime().optional(),
  actualStart: z.string().datetime().optional(),
  actualEnd: z.string().datetime().optional(),
}).refine(
  (data) => Object.values(data).some((v) => v !== undefined),
  { message: 'At least one date field is required' },
);

// ---------------------------------------------------------------------------
// Packing List schemas
// ---------------------------------------------------------------------------

const packingListItemSchema = z.object({
  description: z.string().min(1).max(500),
  partNumber: z.string().max(100).optional(),
  quantity: z.number().int().min(1),
  serialNumbers: z.string().max(2000).optional(),
  notes: z.string().max(500).optional(),
  sortOrder: z.number().int().min(0).optional(),
  cwPurchaseHeaderId: z.number().int().positive().optional(),
  cwPurchaseDetailId: z.number().int().positive().optional(),
  poNumber: z.string().max(50).optional(),
});

export const createPackingListSchema = z.object({
  cwProjectId: z.number().int().positive(),
  title: z.string().min(1).max(300),
  deliveryDate: z.string().optional(),
  deliveryNotes: z.string().max(5000).optional(),
  items: z.array(packingListItemSchema).optional(),
});

export const updatePackingListSchema = z.object({
  title: z.string().min(1).max(300).optional(),
  deliveryDate: z.string().optional(),
  deliveryNotes: z.string().max(5000).optional(),
  items: z.array(packingListItemSchema).optional(),
});

export const importPOSchema = z.object({
  cwPurchaseHeaderId: z.number().int().positive(),
});

export const packingListStatusSchema = z.object({
  status: z.enum(['ready', 'draft', 'delivered']),
});

export const packingListSignSchema = z.object({
  signedByName: z.string().min(1).max(200),
  signedByTitle: z.string().max(200).optional(),
  signatureDataUrl: z.string().min(1),
  signatureMethod: z.enum(['digital', 'photo']).default('digital'),
  items: z
    .array(
      z.object({
        id: z.string().min(1),
        receivedQuantity: z.number().int().min(0).max(1_000_000).optional(),
        exception: z.enum(['ok', 'short', 'damaged', 'missing']).optional(),
        exceptionNote: z.string().max(500).optional(),
      }),
    )
    .optional(),
});

// ---------------------------------------------------------------------------
// Dispatch & Intake schemas
// ---------------------------------------------------------------------------

export const dispatchAssignSchema = z.object({
  ownerId: z.number().int().positive().optional(),
  resources: z.string().optional(),
}).refine((v) => v.ownerId !== undefined || v.resources !== undefined, 'ownerId or resources required');
export const dispatchScheduleSchema = z.object({
  memberId: z.number().int().positive(),
  dateStart: z.string().refine((s) => !Number.isNaN(Date.parse(s)), 'invalid date'),
  durationMinutes: z.number().int().positive().max(1440).default(60),
  statusId: z.number().int().positive().optional(),
  overrideExpiredCoi: z.boolean().optional(),
  overrideConflict: z.boolean().optional(),
});
export const dispatchTriageSchema = z.object({
  priorityId: z.number().int().positive().optional(),
  boardId: z.number().int().positive().optional(),
  statusId: z.number().int().positive().optional(),
}).refine((v) => v.priorityId || v.boardId || v.statusId, 'at least one field required');
export const dispatchNoteSchema = z.object({ text: z.string().min(1), internal: z.boolean().default(true) });
export const dispatchBoardIdsSchema = z.object({ boardIds: z.array(z.number().int().positive()).min(1) });

// Manual subcontractor ProjectAccess grant (#305).
export const projectAccessGrantSchema = z.object({
  userId: z.string().min(1),
  cwPoNumber: z.string().max(100).optional(),
});
// Field Execution schemas
// ---------------------------------------------------------------------------

export const gpsSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});

const withIdempotency = { idempotencyKey: z.string().min(1) };

export const startDaySchema = z.object({
  ...withIdempotency,
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
});

export const startVisitSchema = z.object({
  ...withIdempotency,
  cwTicketId: z.number().int().positive(),
  ticketType: z.enum(['ServiceTicket', 'ProjectTicket']),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
});

// GPS required best-effort: a fix OR an explicit override reason.
const gpsOrOverride = (d: { latitude?: number; longitude?: number; gpsOverrideReason?: string }) =>
  (d.latitude !== undefined && d.longitude !== undefined) || !!d.gpsOverrideReason;

export const arriveSchema = z
  .object({
    ...withIdempotency,
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    gpsOverrideReason: z.string().min(1).optional(),
  })
  .refine(gpsOrOverride, { message: 'GPS fix or an override reason is required' });

export const breakSchema = z.object({
  ...withIdempotency,
  breakType: z.enum(['meal', 'rest', 'other']).default('meal'),
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
});

export const logTimeSchema = z.object({
  ...withIdempotency,
  workTypeId: z.number().int().positive(),
  workRoleId: z.number().int().positive(),
  rawMinutes: z.number().positive(),
  notes: z.string().optional(),
});

export const addProductSchema = z.object({
  ...withIdempotency,
  ticketProductId: z.number().int().positive(),
  price: z.number().min(0),
});

export const addCatalogProductSchema = z.object({
  ...withIdempotency,
  catalogItemId: z.number().int().positive(),
  quantity: z.number().positive().max(9999),
  price: z.number().min(0).optional(),
});

export const visitPhotoSchema = z.object({
  label: z.enum(['before', 'after', 'other']).default('other'),
  /** Client command id — makes offline-queued photo replays idempotent. */
  idempotencyKey: z.string().min(8).max(80).optional(),
});

export const visitSignoffSchema = z.object({
  signedByName: z.string().trim().min(1).max(200),
  signedByTitle: z.string().trim().max(200).optional(),
  signatureDataUrl: z
    .string()
    .max(3_000_000)
    .refine((value) => /^data:image\/png;base64,[A-Za-z0-9+/=]+$/.test(value), {
      message: 'Signature must be a PNG data URL',
    }),
});

export const completeSchema = z
  .object({
    ...withIdempotency,
    workTypeId: z.number().int().positive(),
    workRoleId: z.number().int().positive(),
    notes: z.string().optional(),
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    gpsOverrideReason: z.string().min(1).optional(),
    breakComplianceAck: z.string().optional(),
    scopeOverrideReason: z.string().trim().min(1).max(500).optional(),
    signoffCaptured: z.boolean().default(false),
    signoffExceptionReason: z.string().trim().min(1).max(500).optional(),
  })
  .refine(gpsOrOverride, { message: 'GPS fix or an override reason is required' })
  .refine((value) => value.signoffCaptured || value.signoffExceptionReason, {
    message: 'Customer sign-off or an exception reason is required',
  });

export const endDaySchema = z.object({
  ...withIdempotency,
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  breakComplianceAck: z.string().optional(),
  // opt-in: when the day has in-flight visits (enroute/onsite/on_break) the engine
  // refuses to complete the day unless the caller explicitly forces a void. The UI
  // surfaces a "Void open visit(s) and end day" confirm when this is set (#1099).
  forceVoidOpenVisits: z.boolean().optional(),
});

// POST /field-execution/visits/:id/cancel — "Clock Out" from an in-progress visit (#692).
// No work is logged on a cancel, so we don't need work type/role or GPS here; the engine
// just voids the visit. Latitude/longitude are kept for audit/geo consistency with the
// other visit endpoints but are optional because the tech may legitimately be clocking
// out from inside the building (no fix) or the device may lack a fix.
//
// No `reason` field: the TimeClockAudit table doesn't have a free-text column, and
// the status transition (onsite -> voided) is already captured in before/afterValue.
// Adding a `notes` column would need a migration; defer to a follow-up.
export const cancelVisitSchema = z.object({
  ...withIdempotency,
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
});

export const financeRatesSchema = z.object({
  techBurdenRate: z.number().positive(),
  pmBurdenRate: z.number().positive(),
  pmTimePercent: z.number().min(0).max(1),
  warrantyPercent: z.number().min(0).max(1),
  changeOrderBurdenRate: z.number().positive(),
  // Blended SELL rate ($/hr) used for expected-revenue estimates when detailed
  // CW estimate data is unavailable (e.g. the CW-API-only ASC 606 fallback).
  blendedSellRate: z.number().positive(),
  // Ops monthly billing target ($) — single source for the OpsDashboard goal
  // bar and the 12-month chart goal line, editable without a code deploy (#681).
  monthlyBillingGoal: z.number().positive(),
});

export type FinanceRates = z.infer<typeof financeRatesSchema>;

export const laborModelSchema = z.object({
  mode: z.enum(['internal', 'sub', 'split']),
  // For split: phaseName (scope) -> labor source. Absent scopes default to internal.
  scopes: z.record(z.string(), z.enum(['internal', 'sub'])).optional(),
});
export type LaborModel = z.infer<typeof laborModelSchema>;

export const proposalPhaseEditSchema = z.object({
  phaseName: z.string().min(1),
  team: z.enum(['NOC', 'Field', 'PM']),
  laborSource: z.enum(['internal', 'sub', 'inherit']),
  budgetHours: z.number().min(0),
  plannedStart: z.string().datetime().nullable().optional(),
  plannedEnd: z.string().datetime().nullable().optional(),
}).refine(
  (phase) => !phase.plannedStart || !phase.plannedEnd || phase.plannedStart <= phase.plannedEnd,
  { message: 'Phase planned end must be on or after planned start', path: ['plannedEnd'] },
);

export const proposalEditSchema = z.object({
  projectTypeId: z.number().int().positive().optional(),
  estimatedStart: z.string().optional(),
  estimatedEnd: z.string().optional(),
  phases: z.array(proposalPhaseEditSchema).optional(),
});
export type ProposalEdit = z.infer<typeof proposalEditSchema>;

export const ruleTicketTemplateSchema = z.object({
  summary: z.string().min(1),
  team: z.enum(['NOC', 'Field', 'PM']).optional(),
  checklistItems: z.string().optional(),
  sortOrder: z.number().int().min(0).default(0),
});
export const rulePhaseTemplateSchema = z.object({
  phaseName: z.string().min(1),
  team: z.enum(['NOC', 'Field', 'PM']),
  laborSource: z.enum(['internal', 'sub', 'inherit']).default('inherit'),
  budgetWeight: z.number().min(0).default(1),
  sortOrder: z.number().int().min(0).default(0),
  tickets: z.array(ruleTicketTemplateSchema).default([]),
});
export const productRuleSchema = z.object({
  name: z.string().min(1),
  active: z.boolean().default(true),
  priority: z.number().int().default(100),
  matchField: z.enum(['partNumber', 'itemNumber', 'sku', 'category', 'vendor', 'description']),
  matchType: z.enum(['contains', 'equals', 'startsWith', 'regex']).default('contains'),
  matchPattern: z.string().min(1),
  phases: z.array(rulePhaseTemplateSchema).default([]),
});
export type ProductRuleInput = z.infer<typeof productRuleSchema>;
